import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { sourceSystemService } from "../../../../services/sourceSystemService";
import { useDisplayLocalization } from "../../../../lib/datetime/DisplayLocalizationProvider";
import {
  dashboardOpenSearchService,
  resolveActivityStatusColor,
  type DashboardActivityStatusColor,
  type SourceSystemLineItemRequest,
  type SourceSystemWindowCounts,
} from "../../../../services/dashboardOpenSearchService";
import { dashboardIpFlowService } from "../../../../services/dashboardIpFlowService";
import type { Contact, Source, SourceSystem } from "../../../../types/incident";

export interface SourceSystemListRow {
  rowKey: string;
  sourceSystemId: string;
  sourceSystemName: string;
  definition: string;
  type: string;
  value: string;
  counts: SourceSystemWindowCounts;
  statusColor: DashboardActivityStatusColor;
}

async function loadSourceSystems(customerId: string) {
  const response = await sourceSystemService.getSourceSystemsByCustomerId(
    customerId,
    0,
    200,
  );
  return (response?.content ?? []) as SourceSystem[];
}

function buildLineItemRequests(
  sourceSystems: SourceSystem[],
): SourceSystemLineItemRequest[] {
  const items: SourceSystemLineItemRequest[] = [];

  for (const ss of sourceSystems) {
    const contacts = Array.isArray(ss.contacts) ? ss.contacts : [];
    const sources = Array.isArray(ss.sources) ? ss.sources : [];

    for (const contact of contacts) {
      const rowKey = `${ss.id}-contact-${contact.name}-${contact.value}-${contact.type}`;
      items.push({
        rowKey,
        sourceSystemId: ss.id,
        kind: "contact",
        value: contact.value,
      });
    }

    for (const source of sources) {
      const rowKey = `${ss.id}-source-${source.name}-${source.value}-${source.type}`;
      items.push({
        rowKey,
        sourceSystemId: ss.id,
        kind: "source",
        value: source.value,
      });
    }

    if (contacts.length === 0 && sources.length === 0) {
      items.push({
        rowKey: `${ss.id}-base`,
        sourceSystemId: ss.id,
        kind: "base",
      });
    }
  }

  return items;
}

function buildSourceSystemRows(
  sourceSystems: SourceSystem[],
  stats: Record<string, SourceSystemWindowCounts>,
): SourceSystemListRow[] {
  const rows: SourceSystemListRow[] = [];

  for (const ss of sourceSystems) {
    const contacts = Array.isArray(ss.contacts) ? ss.contacts : [];
    const sources = Array.isArray(ss.sources) ? ss.sources : [];

    const appendRow = (params: {
      rowKey: string;
      definition: string;
      type: string;
      value: string;
    }) => {
      const counts = stats[params.rowKey] ?? emptyCounts();
      rows.push({
        rowKey: params.rowKey,
        sourceSystemId: ss.id,
        sourceSystemName: ss.name ?? "",
        definition: params.definition,
        type: params.type,
        value: params.value,
        counts,
        statusColor: resolveActivityStatusColor(counts, counts),
      });
    };

    for (const contact of contacts) {
      appendRow({
        rowKey: `${ss.id}-contact-${contact.name}-${contact.value}-${contact.type}`,
        definition: contactDefinition(contact),
        type: contact.type ?? "",
        value: typeof contact.value === "string" ? contact.value.trim() : "",
      });
    }

    for (const source of sources) {
      appendRow({
        rowKey: `${ss.id}-source-${source.name}-${source.value}-${source.type}`,
        definition: sourceDefinition(source),
        type: source.type ?? "",
        value: typeof source.value === "string" ? source.value.trim() : "",
      });
    }

    if (contacts.length === 0 && sources.length === 0) {
      appendRow({
        rowKey: `${ss.id}-base`,
        definition: "",
        type: "",
        value: "",
      });
    }
  }

  return rows;
}

function emptyCounts(): SourceSystemWindowCounts {
  return {
    last30Min: 0,
    last1Hour: 0,
    today: 0,
    prevWeek30Min: 0,
    prevWeek1Hour: 0,
    prevWeekToday: 0,
  };
}

function contactDefinition(contact: Contact): string {
  return typeof contact.name === "string" ? contact.name.trim() : "";
}

function sourceDefinition(source: Source): string {
  return typeof source.name === "string" ? source.name.trim() : "";
}

export function useDashboardOverviewPanels(
  customerId: string | null,
  options?: { enabled?: boolean; timeZone?: string },
) {
  const { timeZone: providerTimeZone } = useDisplayLocalization();
  const timeZone = options?.timeZone || providerTimeZone;
  const queryEnabled = options?.enabled !== false && Boolean(customerId);

  const sourceSystemsQuery = useQuery({
    queryKey: ["dashboard-overview", "source-systems", customerId],
    queryFn: () => loadSourceSystems(customerId!),
    enabled: queryEnabled,
    staleTime: 60_000,
  });

  const lineItemRequests = useMemo(
    () => buildLineItemRequests(sourceSystemsQuery.data ?? []),
    [sourceSystemsQuery.data],
  );

  const lineItemRequestsKey = useMemo(
    () =>
      lineItemRequests
        .map((item) => `${item.rowKey}:${item.kind}:${item.value ?? ""}`)
        .join("|"),
    [lineItemRequests],
  );

  const activityStatsQuery = useQuery({
    queryKey: [
      "dashboard-overview",
      "line-item-stats",
      customerId,
      timeZone,
      lineItemRequestsKey,
    ],
    queryFn: () =>
      dashboardOpenSearchService.getSourceSystemLineItemStats(
        customerId!,
        lineItemRequests,
        timeZone,
      ),
    enabled: queryEnabled && lineItemRequests.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const severityQuery = useQuery({
    queryKey: [
      "dashboard-overview",
      "severity-comparison",
      customerId,
      timeZone,
    ],
    queryFn: () =>
      dashboardOpenSearchService.getSeverityDistributionComparison(
        customerId!,
        timeZone,
      ),
    enabled: queryEnabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const severityTimelineQuery = useQuery({
    queryKey: ["dashboard-overview", "severity-timeline", customerId, timeZone],
    queryFn: () =>
      dashboardOpenSearchService.getSeverityTimeline(customerId!, timeZone),
    enabled: queryEnabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const ipFlowQuery = useQuery({
    queryKey: ["dashboard-overview", "ip-flows", customerId],
    queryFn: () => dashboardIpFlowService.getIpFlows(customerId!),
    enabled: queryEnabled,
    staleTime: 4 * 60_000,
    refetchInterval: 5 * 60_000,
  });

  const sourceSystemRows = useMemo(
    () =>
      buildSourceSystemRows(
        sourceSystemsQuery.data ?? [],
        activityStatsQuery.data ?? {},
      ),
    [sourceSystemsQuery.data, activityStatsQuery.data],
  );

  const loading =
    sourceSystemsQuery.isLoading ||
    activityStatsQuery.isLoading ||
    severityQuery.isLoading;

  const error =
    sourceSystemsQuery.error || activityStatsQuery.error || severityQuery.error;

  return {
    customerId,
    sourceSystemRows,
    severityToday: severityQuery.data?.today,
    severityWeekAgo: severityQuery.data?.weekAgo,
    severityTimeline: severityTimelineQuery.data,
    severityTimelineLoading: severityTimelineQuery.isLoading,
    severityTimelineError:
      severityTimelineQuery.error instanceof Error
        ? severityTimelineQuery.error.message
        : severityTimelineQuery.error
          ? String(severityTimelineQuery.error)
          : null,
    ipFlowMatrix: ipFlowQuery.data,
    ipFlowLoading: ipFlowQuery.isLoading,
    loading,
    error:
      error instanceof Error ? error.message : error ? String(error) : null,
    refetch: () => {
      void sourceSystemsQuery.refetch();
      void activityStatsQuery.refetch();
      void severityQuery.refetch();
      void severityTimelineQuery.refetch();
      void ipFlowQuery.refetch();
    },
  };
}
